import { QueryClient, type DefaultOptions } from '@tanstack/react-query'
import { toast } from 'sonner'
import { isApiException } from '@/core/errors'

const handleError = (error: unknown): void => {
  if (isApiException(error)) {
    // Don't show toast for 401 errors (handled by auth interceptor)
    if (error.isUnauthorized) {
      return
    }

    // Show appropriate toast based on error type
    if (error.isNetworkError) {
      toast.error('Error de conexión', {
        description: 'Verifica tu conexión a internet',
      })
      return
    }

    if (error.isTimeout) {
      toast.error('Tiempo de espera agotado', {
        description: 'El servidor no respondió a tiempo',
      })
      return
    }

    if (error.isServerError) {
      toast.error('Error del servidor', {
        description: 'Ocurrió un error interno. Intenta más tarde.',
      })
      return
    }

    // Generic error with message from API
    toast.error('Error', {
      description: error.message,
    })
    return
  }

  // Unknown error
  console.error('Unknown error:', error)
  toast.error('Error inesperado', {
    description: 'Ocurrió un error inesperado',
  })
}

const defaultOptions: DefaultOptions = {
  queries: {
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 30, // 30 minutes (formerly cacheTime)
    retry: (failureCount, error) => {
      // Don't retry on auth errors or validation errors
      if (isApiException(error)) {
        if (error.isUnauthorized || error.isForbidden || error.isValidationError || error.isNotFound) {
          return false
        }
      }
      return failureCount < 2
    },
    refetchOnWindowFocus: false,
  },
  mutations: {
    retry: 0,
    onError: handleError,
  },
}

export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions,
  })
}

export { handleError as queryErrorHandler }
